import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { CheckboxGroupProps } from '@tmapy/style-guide';

export type UseCheckboxGroupArgs<T> = Omit<CheckboxGroupProps<T>, 'values' | 'onChange' | 'renderLabel' | 'renderValue'> & {
  name?: string;
  label?: string;
  initialValue?: T[];
  validator?: (values: T[]) => string | null;
  validate?: boolean;
  errorMessage?: string;
  onChange?(e: React.ChangeEvent, eventData: { values: T[] }): void;
  renderLabel?(item: T): React.ReactNode; 
  renderValue?(item: T): string | number;
};

export type UseCheckboxGroupResult<T> = CheckboxGroupProps<T> & {
  name?: string;
  clear(): void;
  errorMessage: string;
};

export function useCheckboxGroup<T>({
  name,
  initialValue,
  validator,
  validate,
  isInvalid: isExternalInvalid,
  errorMessage: externalErrorMessage,
  onChange: onChangeCallback,
  items,
  renderLabel,
  renderValue,
  ...rest
}: UseCheckboxGroupArgs<T>): UseCheckboxGroupResult<T> {
  const [values, setValues] = useState<T[]>(initialValue || []);

  useEffect(() => {
    setValues(initialValue || []);
  }, [initialValue]);

  const isInvalidRef = useRef(false);

  const onChange = useCallback(
    (e: React.ChangeEvent, eventData: { values: T[] }) => {
        const newValues = eventData.values;
        onChangeCallback?.(e, { values: newValues });
        setValues(newValues);
    },
    [onChangeCallback],
  );

  const clear = useCallback(() => {
    setValues([]);
  }, []);

  const validationResult = useMemo(() => {
    if (validate || isInvalidRef.current) {
      const errorMessage = validator?.(values);
      const isInvalid = !!errorMessage;
      isInvalidRef.current = isInvalid;

      return {
        isInvalid,
        errorMessage: errorMessage ?? '',
      };
    }
    return {
      isInvalid: false,
      errorMessage: '',
    };
  }, [values, validator, validate]);

  return {
    ...rest,
    items,
    values,
    name,
    isInvalid: validationResult.isInvalid || isExternalInvalid || false,
    errorMessage: validationResult.errorMessage || externalErrorMessage || '',
    onChange,
    clear,
    renderLabel: renderLabel!, 
    renderValue: renderValue!,
  };
}
